import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4003';

export interface PublicPlan {
  id: string;
  slug: string;
  name: string;
  type: 'basic' | 'pro' | 'enterprise';
  currency: 'BRL' | 'GBP' | 'USD';
  priceMonthly: string;
  priceYearly?: string;
  description?: string;
  features: string[];
  isActive: boolean;
  displayOrder: number;
  isPopular?: boolean;
}

// Fallback plans when API is not available
const fallbackPlans: Record<string, PublicPlan[]> = {
  BR: [
    {
      id: 'basic-br',
      slug: 'basic-br',
      name: 'Basic',
      type: 'basic',
      currency: 'BRL',
      priceMonthly: '19.90',
      features: [
        '30 fotos de recibo/mês',
        '20 áudios/mês',
        'Textos ilimitados',
        'Dashboard e gráficos básicos',
        'Relatório mensal',
        'Exportar CSV',
        'Retenção de recibos: 1 ano',
      ],
      isActive: true,
      displayOrder: 1,
    },
    {
      id: 'pro-br',
      slug: 'pro-br',
      name: 'Pro',
      type: 'pro',
      currency: 'BRL',
      priceMonthly: '39.90',
      features: [
        'Fotos e áudios ilimitados',
        'Tudo do Basic +',
        'Gráficos e filtros avançados',
        'Comparativo mês a mês',
        'Exportar PDF',
        'Relatório anual (IR)',
        'Suporte prioritário',
        'Retenção de recibos: 2 anos',
      ],
      isActive: true,
      displayOrder: 2,
      isPopular: true,
    },
  ],
  UK: [
    {
      id: 'basic-uk',
      slug: 'basic-uk',
      name: 'Basic',
      type: 'basic',
      currency: 'GBP',
      priceMonthly: '4.99',
      features: [
        '30 receipt photos/month',
        '20 voice notes/month',
        'Unlimited text messages',
        'Dashboard & basic charts',
        'Monthly report',
        'Export CSV',
        'Receipt retention: 1 year',
      ],
      isActive: true,
      displayOrder: 1,
    },
    {
      id: 'pro-uk',
      slug: 'pro-uk',
      name: 'Pro',
      type: 'pro',
      currency: 'GBP',
      priceMonthly: '9.99',
      features: [
        'Unlimited photos & audio',
        'Everything in Basic +',
        'Advanced charts & filters',
        'Month-to-month comparison',
        'Export PDF',
        'Annual tax report',
        'Priority support',
        'Receipt retention: 2 years',
      ],
      isActive: true,
      displayOrder: 2,
      isPopular: true,
    },
  ],
};

const currencyByCountry: Record<string, string> = {
  BR: 'BRL',
  UK: 'GBP',
};

export const publicPlansService = {
  async getPlans(country: 'BR' | 'UK'): Promise<PublicPlan[]> {
    const targetCurrency = currencyByCountry[country] || 'BRL';

    try {
      // Try to fetch from public pricing endpoint
      const { data } = await axios.get<{
        success?: boolean;
        plans?: PublicPlan[];
        byCurrency?: Record<string, PublicPlan[]>;
      }>(
        `${API_URL}/pricing?currency=${targetCurrency}`
      );

      // Handle response - can be { success, plans } or { success, byCurrency }
      const plans = data.plans || data.byCurrency?.[targetCurrency] || [];

      if (plans.length > 0) {
        // Filter by:
        // - is active
        // - has a price > 0 (exclude trial plans)
        // - has features (plans without features aren't ready for display)
        const validPlans = plans.filter(p =>
          p.isActive !== false &&
          parseFloat(p.priceMonthly) > 0 &&
          p.features && p.features.length > 0
        );

        if (validPlans.length > 0) {
          return validPlans.sort((a, b) => a.displayOrder - b.displayOrder);
        }
      }

      // If no valid plans returned, use fallback
      return fallbackPlans[country] || fallbackPlans.BR;
    } catch {
      // API not available, use fallback
      return fallbackPlans[country] || fallbackPlans.BR;
    }
  },
};

export default publicPlansService;
